<script setup>
import { ref, computed } from 'vue';
import {
  getActiveDateRange,
  DATE_RANGE_TYPES,
  CALENDAR_TYPES,
  dateRanges,
} from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  addMonths,
  subMonths,
  isValid,
  format,
  isSameYear,
} from 'date-fns';
import CalendarWeek from 'dashboard/components/ui/DatePicker/components/CalendarWeek.vue';
import CalendarFooter from 'dashboard/components/ui/DatePicker/components/CalendarFooter.vue';

const emit = defineEmits(['dateRangeChanged']);

const { LAST_7_DAYS, CUSTOM_RANGE } = DATE_RANGE_TYPES;
const { START_CALENDAR } = CALENDAR_TYPES;

const currentDate = ref(new Date());
const showPicker = ref(false);
const selectedRange = ref(LAST_7_DAYS);

const selectedStartDate = ref(startOfDay(subDays(new Date(), 6)));
const selectedEndDate = ref(endOfDay(new Date()));
// single calendar current month
const calMonth = ref(startOfMonth(new Date()));
const selectingEndDate = ref(false);
const hoveredEndDate = ref(null);

// endCurrentDate must always be > calMonth for range-highlight helpers
const endCurrentDate = computed(() => addMonths(calMonth.value, 1));

const formatDateRange = computed(() => {
  const s = selectedStartDate.value;
  const e = selectedEndDate.value;
  if (!isValid(s) || !isValid(e)) return 'Selecionar período';
  if (!isSameYear(s, e)) return `${format(s, 'dd/MM/yy')} – ${format(e, 'dd/MM/yy')}`;
  return `${format(s, 'dd/MM')} – ${format(e, 'dd/MM/yy')}`;
});

const SHORTCUT_LABELS = {
  last7days:    '7d',
  last30days:   '30d',
  last3months:  '3m',
  last6months:  '6m',
  lastYear:     '1a',
  thisWeek:     'Semana',
  monthToDate:  'Mês',
};

const shortcuts = dateRanges.filter(r => r.value !== CUSTOM_RANGE);

const applyPreset = (range) => {
  selectedRange.value = range.value;
  const { start, end } = getActiveDateRange(range.value, currentDate.value);
  selectedStartDate.value = start;
  selectedEndDate.value = end;
  calMonth.value = startOfMonth(start);
  selectingEndDate.value = false;
};

const selectDate = (day) => {
  selectedRange.value = CUSTOM_RANGE;
  if (!selectingEndDate.value || day < selectedStartDate.value) {
    selectedStartDate.value = startOfDay(day);
    selectedEndDate.value = null;
    selectingEndDate.value = true;
  } else {
    selectedEndDate.value = endOfDay(day);
    selectingEndDate.value = false;
  }
};

const prevMonth = () => { calMonth.value = subMonths(calMonth.value, 1); };
const nextMonth = () => { calMonth.value = addMonths(calMonth.value, 1); };

const applyRange = () => {
  if (isValid(selectedStartDate.value) && isValid(selectedEndDate.value)) {
    showPicker.value = false;
    emit('dateRangeChanged', [selectedStartDate.value, selectedEndDate.value]);
  }
};

const clearRange = () => {
  selectedRange.value = LAST_7_DAYS;
  const { start, end } = getActiveDateRange(LAST_7_DAYS, currentDate.value);
  selectedStartDate.value = start;
  selectedEndDate.value = end;
  calMonth.value = startOfMonth(start);
  selectingEndDate.value = false;
  showPicker.value = false;
  emit('dateRangeChanged', [start, end]);
};

const closePicker = () => {
  if (showPicker.value) {
    if (isValid(selectedStartDate.value) && isValid(selectedEndDate.value)) {
      emit('dateRangeChanged', [selectedStartDate.value, selectedEndDate.value]);
    }
    showPicker.value = false;
  }
};
</script>

<template>
  <div class="relative contact-datepicker">
    <!-- Trigger button -->
    <button
      class="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 h-8 bg-n-alpha-2 hover:bg-n-alpha-1 transition-colors text-xs font-medium text-n-slate-12 truncate max-w-full"
      @click="showPicker = !showPicker"
    >
      <span class="i-lucide-calendar-range size-3.5 text-n-slate-11 flex-shrink-0" />
      <span class="truncate">{{ formatDateRange }}</span>
      <span class="i-lucide-chevron-down size-3.5 text-n-slate-11 flex-shrink-0" :class="showPicker ? 'rotate-180' : ''" />
    </button>

    <!-- Dropdown — abre para a esquerda para caber no sidebar -->
    <div
      v-if="showPicker"
      v-on-clickaway="closePicker"
      class="absolute top-10 right-0 z-[9999] w-[300px] rounded-2xl shadow-lg select-none bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container overflow-hidden"
    >
      <!-- Shortcuts row -->
      <div class="flex flex-wrap gap-1 px-3 pt-3 pb-2">
        <button
          v-for="range in shortcuts"
          :key="range.value"
          class="px-2 py-0.5 rounded-md text-xs font-medium transition-colors"
          :class="selectedRange === range.value
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'"
          @click="applyPreset(range)"
        >
          {{ SHORTCUT_LABELS[range.value] || range.value }}
        </button>
      </div>

      <div class="border-t border-n-strong mx-2" />

      <!-- Single CalendarWeek -->
      <div class="px-3 py-3">
        <CalendarWeek
          :calendar-type="START_CALENDAR"
          :current-date="currentDate"
          :start-current-date="calMonth"
          :end-current-date="endCurrentDate"
          :selected-start-date="selectedStartDate"
          :selected-end-date="selectedEndDate"
          :selecting-end-date="selectingEndDate"
          :hovered-end-date="hoveredEndDate"
          @update-hovered-end-date="hoveredEndDate = $event"
          @select-date="selectDate"
          @prev="prevMonth"
          @next="nextMonth"
          @set-view="() => {}"
        />
      </div>

      <!-- Hint when selecting end date -->
      <div v-if="selectingEndDate" class="px-4 pb-1 text-xs text-n-slate-10 text-center">
        {{ $t('CONTACT_METRICS.TIMELINE.SELECT_END_DATE') }}
      </div>

      <div class="border-t border-n-strong mx-2" />

      <!-- Footer -->
      <CalendarFooter @clear="clearRange" @change="applyRange" />
    </div>
  </div>
</template>

<style scoped>
/* Garante que o dropdown fique acima do conteúdo ao redor */
.contact-datepicker :deep(.font-inter) {
  font-family: inherit;
}
</style>
